import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { GuestQuoteRequestDone } from '@/features/guest-quote-request';
import { PlainQuoteRequestForm, QuoteRequestUnavailable } from '@/features/quote-request';
import { myQuoteDetailPath } from '@/shared/config';
import { EyebrowLabel, SitePageShell } from '@/shared/ui';
import { GuestPlainQuoteRequestForm } from '@/widgets/guest-quote-request-form';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';
import { usePlainQuoteRequest } from './model/use-plain-quote-request';

/**
 * 일반 견적 요청 페이지(`PLAIN_QUOTE_REQUEST_PATH`/:productId?color=&qty=, 2026-10-30 일반 견적 설계) — 공개 라우트.
 * 편집영역 없는 상품 상세의 [견적 요청]이 이리로 보낸다. 디자인 없이 상품·색상·수량·배송지만 받는다.
 * 로그인했으면 회원 일반 폼(접수 뒤 내 견적 상세로 이동), 아니면 비회원 일반 폼(접수 뒤 같은 자리에 완료 화면 — 결과는 history state 에도 남는다).
 * 주소가 잘못됐으면 안내만, 요청 불가·준비 중이면 회원 요청 화면과 같은 안내를 둔다. 머리는 회원 견적 요청 화면과 같은 결이다.
 */
export const PlainQuoteRequestPage = (): ReactElement => {
  const navigate = useNavigate();
  // 주소 해석·회원/비회원 폼 조회·상품 사진·접수 결과 보관은 페이지 모델 훅이 맡는다 — 페이지는 조합만 한다.
  const {
    params,
    isAuthenticated,
    onboardingHref,
    receipt,
    keepReceipt,
    memberFormQuery,
    guestFormQuery,
    productImageUrl,
  } = usePlainQuoteRequest();
  const formQuery = isAuthenticated ? memberFormQuery : guestFormQuery;

  let body: ReactElement;

  if (receipt !== null) {
    body = <GuestQuoteRequestDone receipt={receipt} />;
  } else if (params === null) {
    body = (
      // 머리의 얇은 아랫선과 겹치지 않게 아랫선만 둔다.
      <div className="flex flex-col items-center border-b border-foreground/10 py-16 text-center">
        <p role="alert" className="text-sm text-destructive">
          잘못된 주소입니다. 상품 상세에서 다시 시도해 주세요.
        </p>
      </div>
    );
  } else if (isAuthenticated && memberFormQuery.data !== undefined) {
    body = (
      <PlainQuoteRequestForm
        form={memberFormQuery.data}
        productImageUrl={productImageUrl}
        initialQuantity={params.quantity}
        onSubmitted={(quoteRequestId) => {
          void navigate(myQuoteDetailPath(quoteRequestId));
        }}
      />
    );
  } else if (!isAuthenticated && guestFormQuery.data !== undefined) {
    body = (
      <GuestPlainQuoteRequestForm
        form={guestFormQuery.data}
        productImageUrl={productImageUrl}
        initialQuantity={params.quantity}
        onSubmitted={keepReceipt}
      />
    );
  } else if (formQuery.isError) {
    body = (
      <QuoteRequestUnavailable
        error={formQuery.error}
        heading="이 상품은 지금 견적을 요청할 수 없습니다."
        onboardingHref={onboardingHref}
        onRetry={() => {
          void formQuery.refetch();
        }}
      />
    );
  } else {
    // 폼과 같은 두 칸 자리표시 — 문구는 화면낭독기에만 읽힌다.
    body = (
      <div>
        <p role="status" className="sr-only">
          견적 요청 정보를 불러오는 중입니다.
        </p>
        <div
          aria-hidden
          className="grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:gap-12 xl:gap-16"
        >
          <div className="flex flex-col gap-6">
            <div className="h-14 w-full animate-pulse bg-muted/70" />
            <div className="h-72 w-full animate-pulse bg-muted/70" />
          </div>
          <div className="min-h-80 animate-pulse bg-muted/70" />
        </div>
      </div>
    );
  }

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      <div className="flex flex-col gap-10 sm:gap-12">
        <header className="flex flex-col gap-5 border-b border-foreground/10 pb-8 lg:flex-row lg:items-end lg:justify-between lg:gap-16">
          <div className="flex flex-col gap-4">
            <EyebrowLabel>Quote Request</EyebrowLabel>
            <h1 className="text-[clamp(2.25rem,4.5vw,4rem)] leading-none font-bold tracking-[-0.03em]">
              견적 요청
            </h1>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-muted-foreground lg:max-w-xl lg:pb-1 lg:text-right">
            {isAuthenticated
              ? '수량과 배송지를 입력하면 임시 견적을 바로 확인하고, 운영자가 검토해 확정 견적서를 발행합니다.'
              : '로그인하지 않아도 연락처만 남기면 담당자가 확인해 연락드려요. 확정 견적은 접수 뒤 받는 견적 링크에서 확인할 수 있어요.'}
          </p>
        </header>
        {body}
      </div>
    </SitePageShell>
  );
};
